import { success } from '@redux-requests/core';
import PDFTronAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { PDFManagerFactory } from 'pdftron';
import { select, takeEvery, put, call, fork } from 'redux-saga/effects';
import { Difference, DocumentTypes } from 'types';
import { inspection, getInspectionId, app } from 'store';
import { renderRectToOpaquePNG } from 'pdftron/utils/thumbnailUtils';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import { trackEvent } from 'components/Tracker/TrackerProvider';
import { getTrackerMetadata } from 'store/trackerMetadata/trackerMetadata';
import {
  updateDifference,
  restoreAllDifferences,
  createMergedDifference,
  startThumbnailUpload,
  unmergeDifference,
  linkEmbeddedImageMatch,
  unlinkEmbeddedImageMatch,
} from './actions';
import { refetchDifferencesAndWait } from './refetchDifferences';
import { clearGraphicThumbnailCacheForDifferenceId } from 'components/pageElements/ResultPage/differenceTypes/graphicThumbnailReadyCache';
import { getDisplayedDifferences } from '../inspections/selectors';
import { fetchGroups, patchGroup } from '../difference-group/actions';
import { fastResyncExcludeDifferences, processResyncResponse, undoLastResync } from '../inspections/actions';

// redraw after restoring difference
function* onRestoreAllDifferencesSuccess() {
  // Yield control to ensure reducers complete before redraw
  yield call(function* () {
    const differences: Difference[] = yield select(getDisplayedDifferences);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  });
}

// redraw after updating difference
function* onUpdateDifferenceSuccess() {
  // Yield control to ensure reducers complete before redraw
  yield call(function* () {
    const differences: Difference[] = yield select(getDisplayedDifferences);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  });
}

function* onLinkEmbeddedImageMatchSuccess() {
  yield call(function* () {
    const differences: Difference[] = yield select(getDisplayedDifferences);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  });
}

function* onUnlinkEmbeddedImageMatchSuccess(action: any) {
  const differenceId = action.meta?.differenceId;
  if (differenceId) {
    clearGraphicThumbnailCacheForDifferenceId(differenceId);
  }

  yield put(inspection.actions.unfocusDifference());

  // The draw-match flow (MarkupZone) creates a new annotation reusing the
  // difference ID, which leaves a duplicate beside the original placeholder.
  // Delete ALL annotations for this difference so drawAllTexts can recreate
  // the correct one from scratch — mirrors what a full page reload does.
  if (differenceId) {
    [DocumentTypes.source, DocumentTypes.target].forEach((docType) => {
      const instance = PDFTronManager.getPDFInstance(docType);
      if (instance) {
        const annotationManager = instance.Core.documentViewer.getAnnotationManager();
        const dupes = annotationManager.getAnnotationsList().filter((annot) => annot.Id === differenceId);
        if (dupes.length) {
          annotationManager.deleteAnnotations(dupes, { imported: true, force: true });
        }
      }
    });
  }

  yield call(function* () {
    const differences: Difference[] = yield select(getDisplayedDifferences);
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();
    if (pdfDocManager) {
      pdfDocManager.target?.removeHighContrasts();
      pdfDocManager.source?.removeHighContrasts();
    }
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  });

  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  if (pdfDocManager) {
    const { zoomLockLevel } = useZoomStateStore.getState();
    if (!zoomLockLevel[DocumentTypes.source]) {
      pdfDocManager.source?.fitToPage();
    }
    if (!zoomLockLevel[DocumentTypes.target]) {
      pdfDocManager.target?.fitToPage();
    }

    useZoomStateStore.getState().setZoomChanging();
  }
}

// redraw after updating group difference
function* onPatchGroupSuccess() {
  // Yield control to ensure reducers complete before redraw
  yield call(function* () {
    const differences: Difference[] = yield select(getDisplayedDifferences);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  });
}

// redraw after unmerging differences
function* onUnmergeDifferenceSuccess(action: any) {
  // Get merged difference ID from action metadata (passed from action creator)
  const mergedDifferenceId = action.meta?.mergedDifferenceId;

  // Remove merged difference annotation immediately before state mutations
  // because the mutations will remove it from state
  if (mergedDifferenceId) {
    [DocumentTypes.source, DocumentTypes.target].forEach((docType) => {
      const instance = PDFTronManager.getPDFInstance(docType);
      if (instance) {
        const annotationManager = instance.Core.documentViewer.getAnnotationManager();

        const annotation = annotationManager.getAnnotationById(mergedDifferenceId);
        if (annotation) {
          annotationManager.deleteAnnotation(annotation, { imported: true, force: true });
        }
      }
    });
  }

  yield put(inspection.actions.unfocusDifference());
  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  if (pdfDocManager) {
    pdfDocManager.target?.removeHighContrasts();
    pdfDocManager.source?.removeHighContrasts();

    // Reset zoom to default (fit to page) on both documents
    const { zoomLockLevel } = useZoomStateStore.getState();
    if (!zoomLockLevel[DocumentTypes.source]) {
      pdfDocManager.source?.fitToPage();
    }
    if (!zoomLockLevel[DocumentTypes.target]) {
      pdfDocManager.target?.fitToPage();
    }

    useZoomStateStore.getState().setZoomChanging();
  }

  // Clean up any remaining merge zone annotations to complete the reversal
  yield* cleanupMergeZones();

  // Yield control to ensure reducers complete before redraw (smoothest UX)
  yield call(function* () {
    const differences: Difference[] = yield select(getDisplayedDifferences);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  });
}

// Redraw after re-inspect only if the payload indicates a real success
export function* onReinspectSuccess(action: any) {
  try {
    const updatedList = action?.response?.data?.updatedDifferenceList;
    if (!Array.isArray(updatedList)) {
      // Not a valid reinspect success payload; skip redraw
      return;
    }
  } catch (_e) {
    return;
  }

  // Re-inspect creates and removes groups server-side (insertCTFDifferences auto-groups the new
  // text differences). The returned differences carry those new group ids, so the groups query has
  // to be refetched or the UI holds differences pointing at groups it has never fetched — which
  // costs them their group chip and range label, and breaks focus.
  const inspectionId: string = yield select(getInspectionId);
  if (inspectionId) {
    yield put(fetchGroups(inspectionId));
  }

  yield call(function* () {
    const differences: Difference[] = yield select(getDisplayedDifferences);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  });
}

// Helper function to clean up merge zone annotations
function* cleanupMergeZones() {
  [DocumentTypes.source, DocumentTypes.target].forEach((docType) => {
    const instance = PDFTronManager.getPDFInstance(docType);
    if (instance) {
      const annotationManager = instance.Core.documentViewer.getAnnotationManager();
      const allAnnotations = annotationManager.getAnnotationsList();

      const mergeZoneAnnotations = allAnnotations.filter((annot: any) => {
        return annot.getCustomData && annot.getCustomData('isMergeZone') === 'true';
      });

      if (mergeZoneAnnotations.length > 0) {
        annotationManager.deleteAnnotations(mergeZoneAnnotations, { imported: true, force: true });

        // Force immediate refresh to ensure draw boxes disappear
        if (instance.Core && instance.Core.documentViewer) {
          instance.Core.documentViewer.refreshAll();
          instance.Core.documentViewer.updateView();
        }
      }
    }
  });
}

// redraw after creating merged difference
function* onCreateMergedDifferenceSuccess(action: any) {
  try {
    // Clean up merge zone annotations immediately
    try {
      yield* cleanupMergeZones();
    } catch (cleanupError) {
      console.error('Failed to cleanup merge zone annotations:', cleanupError);
      // Continue execution as this is not critical for the merge operation
    }

    // Get the newly created difference ID from the action response
    const newDifferenceId = action.response?.data?.id || action.payload?.data?.id || action.data?.id;

    if (!newDifferenceId) {
      throw new Error('Failed to extract new difference ID from merge response');
    }

    // Refetch to update Redux state, waiting for it to settle before thumbnails are generated
    const inspectionId: string = yield select(getInspectionId);

    if (!inspectionId) {
      throw new Error('Failed to get inspection ID for difference refetch');
    }

    try {
      yield call(refetchDifferencesAndWait, inspectionId);
    } catch (fetchError) {
      console.error('Failed to refetch differences after merge:', fetchError);
      throw new Error('Could not update differences list after merge completion');
    }

    // Yield control to ensure reducers complete before redraw (smoothest UX)
    yield call(function* () {
      try {
        // Get only displayed differences (excludes original components of merged differences)
        const differences: Difference[] = yield select(getDisplayedDifferences);
        const newDifference = differences.find((diff) => diff.id === newDifferenceId);

        if (!newDifference) {
          throw new Error(`Newly merged difference with ID ${newDifferenceId} not found in updated state`);
        }

        try {
          // Draw all visible differences to ensure proper state
          PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
          PDFTronAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
        } catch (drawError) {
          console.error('Failed to redraw annotations after merge:', drawError);
          throw new Error('Could not redraw difference annotations');
        }

        // Optimistic UI: set loading thumbnails immediately to avoid flicker
        try {
          yield put(
            updateDifference(newDifference.id, {
              thumbnailPaths: {
                [DocumentTypes.source]: { thumbnailUrl: undefined },
                [DocumentTypes.target]: { thumbnailUrl: undefined },
              },
            }),
          );
        } catch (prepError) {
          console.warn('Unable to set initial loading thumbnails for merged difference:', prepError);
        }

        // Start thumbnail generation in background (do not block focus)
        try {
          yield fork(generateMergedDifferenceThumbnails, newDifference, inspectionId);
        } catch (thumbnailError) {
          console.error('Failed to start thumbnail generation for merged difference:', thumbnailError);
        }

        // Immediately focus and zoom to the merged difference for snappier UX
        try {
          yield* focusAndZoomToMergedDifference(newDifference);
        } catch (focusError) {
          console.error('Failed to focus merged difference:', focusError);
        }

        try {
          // Track audit event for merged differences
          const trackerMetadata: any = yield select(getTrackerMetadata);
          if (!trackerMetadata?.userEmail) {
            console.warn('Missing user email for audit tracking - audit event may be incomplete');
          }

          trackEvent({
            name: 'differences-merged',
            data: {
              actingUserEmail: trackerMetadata.userEmail,
              inspectionId: inspectionId,
              mergedDifferenceId: newDifferenceId,
              numberOfDifferencesMerged: newDifference.originalDifferenceIds?.length || 2,
              sourceCoordinates: newDifference?.source?.location?.rect || null,
              newCoordinates: newDifference?.target?.location?.rect || null,
            },
          });
        } catch (trackingError) {
          console.error('Failed to track merged differences audit event:', trackingError);
          // Continue execution as tracking failure should not block the merge
        }
      } catch (innerError) {
        console.error('Critical error in merge post-processing:', innerError);
        throw innerError; // Re-throw to trigger outer catch
      }
    });
  } catch (error) {
    console.error('Critical failure in merge success saga - merge may be incomplete:', error);
    // Show user notification for critical failures using Redux pattern
    yield put(
      app.actions.setSnackMessage({
        message: 'Failed to complete merge operation. Please try again or contact support if the issue persists.',
        type: 'error',
      }),
    );
  }
}

// Generate thumbnails for merged difference from annotation bounds using S3 upload
function* generateMergedDifferenceThumbnails(
  difference: Difference,
  inspectionId: string,
): Generator<any, void, unknown> {
  try {
    // Get instances for both documents
    const sourceInstance = PDFTronManager.getPDFInstance(DocumentTypes.source);
    const targetInstance = PDFTronManager.getPDFInstance(DocumentTypes.target);

    if (!sourceInstance || !targetInstance) {
      console.warn('Could not get PDF instances for thumbnail generation');
      return;
    }

    // Set initial loading state for thumbnails in database (undefined = loading)
    // This ensures the UI shows loading shimmer until thumbnails are uploaded
    yield put(
      updateDifference(difference.id, {
        thumbnailPaths: {
          [DocumentTypes.source]: { thumbnailUrl: undefined },
          [DocumentTypes.target]: { thumbnailUrl: undefined },
        },
      }),
    );

    // Extract and upload thumbnails for both documents
    yield* uploadThumbnailForDocument(sourceInstance, difference, DocumentTypes.source, inspectionId);
    yield* uploadThumbnailForDocument(targetInstance, difference, DocumentTypes.target, inspectionId);
  } catch (error) {
    console.error('Error generating merged difference thumbnails:', error);
  }
}

// Helper function to convert rect [x, y, width, height] to PDFTron Rect
function rectToPDFTronRect(instance: any, rect: number[]) {
  const [x, y, width, height] = rect;
  return new instance.Core.Math.Rect(x, y, x + width, y + height);
}

// Extract thumbnail and upload to S3 for a specific document
function* uploadThumbnailForDocument(
  instance: any,
  difference: Difference,
  documentType: DocumentTypes,
  inspectionId: string,
): Generator<any, void, unknown> {
  try {
    const doc = instance.Core.documentViewer.getDocument();
    const location = documentType === DocumentTypes.source ? difference.source.location : difference.target.location;

    // Compute an expanded render rect so the annotation box will be visible within the thumbnail
    const { expandedRect, normalizedOverlayRect } = getExpandedRectWithOverlay(instance, location);

    // Extract thumbnail using loadCanvas and draw an annotation overlay rectangle
    const thumbnailBlob = yield call(renderRectToOpaquePNG, doc, location.pageNumber, expandedRect, instance, 2);

    if (thumbnailBlob) {
      // Upload to S3 using the same pattern as MarkupZone.ts
      yield put(startThumbnailUpload(inspectionId, difference.id, thumbnailBlob, documentType));
    } else {
      console.warn(`No thumbnail blob generated for ${documentType}`);

      // Mark only this side as failed; updateDifference merges per document type, so the other
      // side's thumbnail is left untouched whether or not its upload has finished yet.
      yield put(
        updateDifference(difference.id, {
          // null indicates failed, undefined indicates loading
          thumbnailPaths: { [documentType]: { thumbnailUrl: null } },
        }),
      );
    }
  } catch (error) {
    console.error(`Error uploading thumbnail for ${documentType}:`, error);

    // As above: only this side is marked failed, the other side is merged rather than replaced.
    yield put(
      updateDifference(difference.id, {
        // null indicates failed, undefined indicates loading
        thumbnailPaths: { [documentType]: { thumbnailUrl: null } },
      }),
    );
  }
}

// Expand the original difference rect by a margin (relative padding) while clamping to page bounds.
// Returns both the expanded rect (PDFTron Rect) and the normalized overlay rect (annotation bounds relative to expanded rect).
function getExpandedRectWithOverlay(instance: any, location: { rect: number[]; pageNumber: number }) {
  const [rx, ry, rwidth, rheight] = location.rect;
  // Normalize potential negative widths/heights to get a canonical left/top/width/height
  const originalRect = {
    x: rwidth < 0 ? rx + rwidth : rx,
    y: rheight < 0 ? ry + rheight : ry,
    width: Math.abs(rwidth),
    height: Math.abs(rheight),
  };

  // Page bounds for clamping
  const pageInfo = instance.Core.documentViewer.getDocument().getPageInfo(location.pageNumber);
  const pageWidth = pageInfo.width;
  const pageHeight = pageInfo.height;

  const padding = Math.max(10, Math.round(Math.max(originalRect.width, originalRect.height) * 0.2));

  const expanded = {
    x: Math.max(0, originalRect.x - padding),
    y: Math.max(0, originalRect.y - padding),
    width: Math.min(pageWidth, originalRect.x + originalRect.width + padding) - Math.max(0, originalRect.x - padding),
    height:
      Math.min(pageHeight, originalRect.y + originalRect.height + padding) - Math.max(0, originalRect.y - padding),
  };

  // Normalized overlay rectangle (annotation rect within expanded rect)
  // Convert from PDF bottom-left origin to canvas top-left origin
  const normalizedOverlayRect = {
    x: (originalRect.x - expanded.x) / expanded.width,
    y: 1 - (originalRect.y - expanded.y + originalRect.height) / expanded.height,
    width: originalRect.width / expanded.width,
    height: originalRect.height / expanded.height,
  };

  const expandedRect = new instance.Core.Math.Rect(
    expanded.x,
    expanded.y,
    expanded.x + expanded.width,
    expanded.y + expanded.height,
  );
  return { expandedRect, normalizedOverlayRect };
}

// Legacy signature preserved for compatibility; delegate to centralized util
function extractThumbnailBlobWithOverlay(
  doc: any,
  pageNumber: number,
  renderRect: any,
  _normalizedOverlayRect?: { x: number; y: number; width: number; height: number },
  _documentType?: DocumentTypes,
  instance?: any,
): Promise<Blob | null> {
  return renderRectToOpaquePNG(doc, pageNumber, renderRect, instance, 2);
}

// Helper saga for focusing and zooming (mimics differenceAnnotationSelected pattern)
function* focusAndZoomToMergedDifference(difference: Difference) {
  try {
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();
    if (!pdfDocManager) return;

    // Zoom first (before focusing) - following established pattern
    pdfDocManager.zoomToDifference(difference);

    // Focus the difference
    yield put(inspection.actions.focusDifference(difference.id));

    // Scroll after focusing
    pdfDocManager.scrollToDifferences(difference);

    // Update zoom state
    useZoomStateStore.getState().setZoomChanging();

    // No delayed redraw needed - annotations are drawn in parallel above
    // The handleFocusDifference saga will handle highlighting automatically
  } catch (error) {
    console.error('Error focusing merged difference:', error);
  }
}

export default [
  takeEvery(success(restoreAllDifferences.toString()), onRestoreAllDifferencesSuccess),
  takeEvery(success(updateDifference.toString()), onUpdateDifferenceSuccess),
  takeEvery(success(linkEmbeddedImageMatch.toString()), onLinkEmbeddedImageMatchSuccess),
  takeEvery(success(patchGroup.toString()), onPatchGroupSuccess),
  takeEvery(success(processResyncResponse.toString()), onReinspectSuccess),
  takeEvery(success(fastResyncExcludeDifferences.toString()), onReinspectSuccess),
  takeEvery(success(undoLastResync.toString()), onReinspectSuccess),
  takeEvery(success(createMergedDifference.toString()), onCreateMergedDifferenceSuccess),
  takeEvery(success(unmergeDifference.toString()), onUnmergeDifferenceSuccess),
  takeEvery(success(unlinkEmbeddedImageMatch.toString()), onUnlinkEmbeddedImageMatchSuccess),
];
